import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Contact, Eye, EyeOff, Wallet, History, AlertTriangle } from 'lucide-react'
import api from '../api'
import { Card, PageHeader, Badge, EmptyState, Button, Table, THead, td, Pagination } from '../components/ui'

const STATUS_TONE = { AKTIF: 'success', ISOLIR: 'warning', BERHENTI: 'danger' }
const TAGIHAN_TONE = { LUNAS: 'success', SEBAGIAN: 'warning', BELUM_LUNAS: 'danger' }

function rp(n) { return 'Rp ' + Number(n || 0).toLocaleString('id-ID') }
function fmtTanggal(t) { return t ? new Date(t + 'T00:00:00').toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' }) : '-' }
function maskNik(nik) { return nik ? nik.slice(0, 4) + '••••••••' + nik.slice(-2) : '-' }

// Client Area (2026-07-29) -- halaman tersendiri per pelanggan (BUKAN modal), supaya bisa dibuka
// tab baru & lebih lega utk tabel history pembayaran yg panjang. Gabungan 3 sumber data yg
// sebelumnya kepisah (modal Detail di PelangganKontak.jsx, halaman Tagihan, halaman History
// Pembayaran) jadi SATU tempat "tracking" per pelanggan -- tujuan eksplisit user: mempercepat
// admin/teknisi cek riwayat 1 pelanggan tanpa loncat antar halaman & filter manual by username.
export default function ClientArea() {
  const { username } = useParams()
  const navigate = useNavigate()

  const [data, setData] = useState(null)
  const [ringkasanTagihan, setRingkasanTagihan] = useState(null)
  const [saldoDompet, setSaldoDompet] = useState(0)
  const [linkTagihan, setLinkTagihan] = useState('')
  const [loading, setLoading] = useState(true)
  const [showNik, setShowNik] = useState(false)

  const [riwayat, setRiwayat] = useState([])
  const [riwayatTotal, setRiwayatTotal] = useState(0)
  const [riwayatLoading, setRiwayatLoading] = useState(true)
  const [page, setPage] = useState(1)
  const limit = 20

  useEffect(() => { load() }, [username])
  useEffect(() => { loadRiwayat() }, [username, page])

  async function load() {
    setLoading(true)
    try {
      const [detailRes, ringkasanRes, dompetRes, linkRes] = await Promise.all([
        api.get(`/pelanggan-kontak/${encodeURIComponent(username)}`),
        api.get(`/tagihan/pelanggan/${encodeURIComponent(username)}/ringkasan`),
        api.get(`/pembayaran/dompet/${encodeURIComponent(username)}`),
        api.get(`/pelanggan-kontak/${encodeURIComponent(username)}/link-tagihan`)
      ])
      setData(detailRes.data)
      setRingkasanTagihan(ringkasanRes.data)
      setSaldoDompet(dompetRes.data.saldo)
      setLinkTagihan(linkRes.data.url)
    } finally { setLoading(false) }
  }

  async function loadRiwayat() {
    setRiwayatLoading(true)
    try {
      const res = await api.get('/pembayaran', { params: { ppp_username: username, page, limit } })
      setRiwayat(res.data.items)
      setRiwayatTotal(res.data.total)
    } finally { setRiwayatLoading(false) }
  }

  if (loading) {
    return <div><PageHeader title="Client Area" /><EmptyState icon={Contact} title="Memuat..." /></div>
  }
  if (!data) {
    return <div><PageHeader title="Client Area" /><EmptyState icon={Contact} title="Pelanggan tidak ditemukan" /></div>
  }

  return (
    <div>
      <PageHeader
        title={data.nama || data.ppp_username}
        subtitle={`Client Area — ${data.ppp_username}`}
        actions={<Button variant="neutral" icon={ArrowLeft} onClick={() => navigate('/pelanggan-kontak')}>Kembali</Button>}
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 1fr) minmax(280px, 1.4fr)', gap: 16, marginBottom: 16 }}>
        {/* Data Diri */}
        <Card style={{ padding: 20 }}>
          <h2 style={{ fontSize: 14.5, fontWeight: 700, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Contact size={16} color="var(--brand)" /> Data Diri
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13 }}>
            <DetailRow label="Username PPP" value={data.ppp_username} />
            <DetailRow label="Nomor HP" value={data.no_hp} />
            <DetailRow label="Alamat" value={data.alamat} />
            <DetailRow label="Paket" value={data.nama_paket} />
            <DetailRow label="Metode Bayar" value={data.metode_bayar === 'PRABAYAR' ? 'Prabayar' : 'Pascabayar'} />
            <DetailRow label="Tgl Jatuh Tempo" value={data.tanggal_jatuh_tempo ? `Tanggal ${data.tanggal_jatuh_tempo}` : '-'} />
            <DetailRow label="Status" value={<Badge tone={STATUS_TONE[data.status] || 'neutral'}>{data.status || 'AKTIF'}</Badge>} raw />
            {data.status_diubah_at && <DetailRow label="Status Sejak" value={data.status_diubah_at} />}
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 2 }}>NIK</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontFamily: 'monospace' }}>{data.nik ? (showNik ? data.nik : maskNik(data.nik)) : '-'}</span>
                {data.nik && (
                  <button onClick={() => setShowNik(s => !s)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                    {showNik ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                )}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 4 }}>Saldo Dompet</div>
              <div style={{ fontWeight: 700, fontSize: 15, color: saldoDompet > 0 ? 'var(--success)' : 'var(--text)' }}>{rp(saldoDompet)}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 4 }}>Link Tagihan Publik</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 11.5, color: 'var(--text-muted)', wordBreak: 'break-all' }}>{linkTagihan}</span>
                <Button size="sm" variant="soft" onClick={() => navigator.clipboard?.writeText(linkTagihan)} style={{ flexShrink: 0 }}>Salin</Button>
              </div>
            </div>
          </div>
        </Card>

        {/* Ringkasan Tagihan */}
        <Card style={{ padding: 20 }}>
          <h2 style={{ fontSize: 14.5, fontWeight: 700, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertTriangle size={16} color="var(--brand)" /> Ringkasan Tagihan
          </h2>
          <div style={{ display: 'flex', gap: 20, marginBottom: 16, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 4 }}>Belum Lunas</div>
              <div style={{ fontWeight: 700, fontSize: 18, color: ringkasanTagihan.total_belum_lunas > 0 ? 'var(--danger)' : 'var(--success)' }}>{rp(ringkasanTagihan.total_belum_lunas)}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 4 }}>Tagihan Terbuka</div>
              <div style={{ fontWeight: 700, fontSize: 18 }}>{ringkasanTagihan.jumlah_tagihan_terbuka}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 4 }}>Lewat Jatuh Tempo</div>
              <div style={{ fontWeight: 700, fontSize: 18, color: ringkasanTagihan.jumlah_overdue > 0 ? 'var(--danger)' : 'var(--text)' }}>{ringkasanTagihan.jumlah_overdue}</div>
            </div>
          </div>

          {ringkasanTagihan.tagihan_terbuka.length === 0 ? (
            <p style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Tidak ada tagihan terbuka -- semua lunas.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {ringkasanTagihan.tagihan_terbuka.map(t => (
                <div key={t.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', borderRadius: 8, background: 'var(--surface-muted, #f7f7fa)', fontSize: 12.5 }}>
                  <div>
                    <span style={{ fontWeight: 600 }}>Periode {t.periode}</span>
                    <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>Jatuh tempo {fmtTanggal(t.jatuh_tempo)}</span>
                    {!!t.konfirmasi_keterlambatan && <Badge tone="neutral" style={{ marginLeft: 8 }}>Dikonfirmasi</Badge>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span>{rp(t.total_tagihan - t.total_dibayar)}</span>
                    <Badge tone={TAGIHAN_TONE[t.status]}>{t.status.replace('_', ' ')}</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* History Pembayaran */}
      <h2 style={{ fontSize: 14.5, fontWeight: 700, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
        <History size={16} color="var(--brand)" /> History Pembayaran
      </h2>
      <Table>
        <THead cols={['Tanggal', 'Jumlah', 'Metode/Jenis', 'Keterangan', 'Dikonfirmasi Oleh', 'Terkait']} />
        <tbody>
          {riwayatLoading ? (
            <tr><td colSpan={6}><EmptyState icon={History} title="Memuat..." /></td></tr>
          ) : riwayat.length === 0 ? (
            <tr><td colSpan={6}><EmptyState icon={History} title="Belum ada riwayat pembayaran" /></td></tr>
          ) : riwayat.map(item => (
            <tr key={`${item.sumber}-${item.id}`}>
              <td style={td}>{fmtTanggal(item.tanggal)}</td>
              <td style={td}>{rp(item.jumlah)}</td>
              <td style={td}>{item.metode || '-'}</td>
              <td style={td}>{item.keterangan || '-'}</td>
              <td style={td}>{item.dicatat_oleh || '-'}</td>
              <td style={td}>
                {item.periode_terkait
                  ? <Badge tone="brand">Tagihan {item.periode_terkait}</Badge>
                  : <Badge tone="success">Deposit</Badge>}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <Card style={{ marginTop: 0, borderTop: 0, borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
        <Pagination page={page} limit={limit} total={riwayatTotal} onChange={setPage} />
      </Card>
    </div>
  )
}

function DetailRow({ label, value, raw }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 2 }}>{label}</div>
      <div>{raw ? value : (value || '-')}</div>
    </div>
  )
}
